<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import Header from './components/Header.vue'
import Main from './components/Main.vue'
import Footer from './components/Footer.vue'
import Spinner from './components/Spinner.vue'
import Thankyou from './components/Thankyou.vue'
import { advertiserMapping } from './constants/selectOptions.js'

// Stores
import { useSessionStore } from '@/stores/sessionStore'
import { useFormStore } from '@/stores/formStore'
import { useLeadStore } from '@/stores/leadStore'

// Services
import { fetchLocation } from '@/services/locationService'
import { fetchArrayData, parseArrayResponse } from '@/services/arrayService'
import { trackInitialImpressionOnce } from '@/services/trackingService'
import { sendEmailSignup, sendPhoneUpdate, sendAddressUpdate, sendArrayData, sendBuyerInfo } from '@/services/blueshiftDataService'
import { triggerFafSignup, triggerFullSubmit, triggerBuyerSequence } from '@/services/blueShiftService'
import { sendToCallCenter, shouldSendToCallCenter } from '@/services/callCenterService'
import { fireLpPixel, initTrustedForm, initRedTrackPixel } from '@/services/pixelService'
import {
  clearEverflowClickError,
  appendEverflowDebugLog,
  clearEverflowDebugLog,
  clearEverflowSdkError,
  clearStoredEverflowTransactionId,
  initEverflow,
  initEverflowWithClickTracking,
  loadLpClickScript,
  recoverEverflowTransactionId
} from '@/services/everflowService'
import { buildLeadData, getAdvertiserFromClients, processLead } from '@/services/leadService'

// Config
import { getCampaignKeysForAff, getCreditPullSettingsForCampaign, DISCLAIMER_AIDS, SKIP_CREDIT_AIDS } from '@/config/campaignKeys'
import { getQuestionConfig, getQuestionOrder } from '@/config/questions'

// Utils
import { sliderToDollarValue, sliderToNumberValue, calculateSliderGradient, toIsoDob, debtAmountToSliderValue } from '@/utils/formatters'
import { isBlankOrUnknown } from '@/utils/validators'

// Initialize stores
const sessionStore = useSessionStore()
const formStore = useFormStore()
const leadStore = useLeadStore()

// Expose stores to window for console debugging
if (import.meta.env.DEV) {
  window.sessionStore = sessionStore
  window.formStore = formStore
  window.leadStore = leadStore
}

// Initialize session
sessionStore.initialize()

// URL parameters
const params = new URLSearchParams(window.location.search)
const isSpMode = params.get('sp') === '1'
const isShortForm = params.get('sf') === '1'
const shortFormIncome = 2500
const amountFromDebtParam = debtAmountToSliderValue(params.get('da'))
const previewParam = params.get('preview')
const previewAdvertiserParam = params.get('adv') || params.get('advertiser')
const thankYouPreviewAdvertiserId =
  (
    params.get('ty') === '1' ||
    params.get('thankyou') === '1' ||
    previewParam === '1' ||
    previewParam === 'ty' ||
    previewParam === 'thankyou'
  ) && previewAdvertiserParam && advertiserMapping[String(previewAdvertiserParam)]
    ? String(previewAdvertiserParam)
    : null
const isThankYouPreview = !!thankYouPreviewAdvertiserId

// If da is present and numeric, use it as the initial amount value
if (amountFromDebtParam !== null) {
  formStore.values.amount = amountFromDebtParam
}

if (isShortForm) {
  formStore.values.income = shortFormIncome
}

// Check for direct link affiliates that should redirect to FTC
const checkDirectLinkRedirect = () => {
  const affId = sessionStore.affiliateId
  if (affId === 'dlf' || affId === 'dlt') {
    window.location.href = 'https://consumer.ftc.gov/articles/how-get-out-debt'
    return true
  }
  return false
}

// Track initial impression
if (!isThankYouPreview) {
  trackInitialImpressionOnce({
    sessionId: sessionStore.sessionId,
    variantId: sessionStore.variantId,
    sourceId: sessionStore.affiliateId
  })
}

// Computed properties
const mainHeadline = computed(() => {
  if (isSpMode) {
    return 'New "Payment Relief" Program Gives Those With $20,000 or More in Debt a Fresh Start'
  }

  if (window.location.hostname.includes('seniors')) {
    return 'Seniors Could Qualify for Debt Relief'
  }
  return 'Find Out If You Qualify For Economic Debt Relief in 2026'
})

const mainSubHeadline = computed(() => {
  if (isSpMode) {
    return null
  }

  if (window.location.hostname.includes('seniors')) {
    return "A lifetime of hard work shouldn't come with saddlebags of debt. Get back the retirement you were meant to have."
  }
  return 'Your search for debt relief begins here!'
})

const requiresAddressFallback = ref(false)
const questionOrder = ref(getQuestionOrder({ shortForm: isShortForm }))
const currentProgress = ref(0)
const transitionDirection = ref('forward')
const isRemovingQuestion = ref(false) // Boolean for transition animation
const isSubmittingQuestion = ref(false)
const showSpinner = ref(false)
const displayThankYou = ref(false)
const backButtonVisible = ref(false)
const trackingDisclosureVisible = ref(false)
const showArrayDisclaimer = ref(false)
const advertiser = ref('0')
const postAttemptNumber = ref(1)
const lpRequestId = ref(null)
const everflowInitPromise = ref(null)

// Array data storage
const arrayData = ref({
  collections: null,
  credit_score: null,
  delinquencies: null,
  total_unsecured_debt: null,
  tax_liens: null,
  open_student_loan_bal: null
})

// Reactive question state that components can modify
// This bridges between the static config and the mutable component state
const questionState = reactive({
  // Simple value questions
  amount: { value: formStore.values.amount },
  income: { value: formStore.values.income },
  employed: { value: formStore.values.employed },

  // Name question
  name: {
    first: formStore.name.first,
    last: formStore.name.last
  },

  // Email question
  email: {
    value: formStore.email.value,
    sendtoAC: formStore.email.sendtoAC
  },

  // Address question
  address: {
    address: formStore.address.address,
    zip: formStore.address.zip,
    city: formStore.address.city,
    state: formStore.address.state,
    overrideValidation: false
  },

  // DOB question (with maska support for input masking)
  dob: {
    day: formStore.dob.day,
    month: formStore.dob.month,
    year: formStore.dob.year,
    dayMaskaObject: { completed: false },
    monthMaskaObject: { completed: false },
    yearMaskaObject: { completed: false }
  },

  // Phone/Contact question (with maska support)
  phone: {
    value: formStore.phone.value,
    maskaObject: { unmasked: '', completed: false },
    tcpaLanguage: '',
    validationStatus: 'pending',
    overrideValidation: false
  }
})

// Expose additional state for console debugging
if (import.meta.env.DEV) {
  window.questionState = questionState
  window.arrayData = arrayData
}

// Get current question ID
const currentQuestionId = computed(() => questionOrder.value[currentProgress.value])

// Dynamic chat message for name question (must be defined before the watch that uses it)
const nameChatMessage = computed(() => {
  const debt = sliderToDollarValue(questionState.amount.value)
  const income = Number(questionState.income.value || 0)

  if (isShortForm) {
    return `<p>It looks like you have ${debt} in debt. Let's see what kind of savings we can find for you.</p>`
  }

  if (income === 0) {
    return `<p>It looks like you have ${debt} in debt and no current income. I understand times can be tough, but don't worry – there are still options available to help you get back on your feet.</p>`
  }
  return `<p>Great! It looks like you have ${debt} in debt and your income is around $${income.toLocaleString()} per month. Let's see what kind of savings we can find for you!</p>`
})

function setShortFormIncome() {
  if (!isShortForm) return

  questionState.income.value = shortFormIncome
  formStore.setValue('income', shortFormIncome)
  localStorage.setItem('income', JSON.stringify(shortFormIncome))
}

function hasLocationData() {
  const zip = questionState.address.zip || sessionStore.backupZip
  const city = questionState.address.city || sessionStore.backupCity
  const state = questionState.address.state || sessionStore.backupState

  return !isBlankOrUnknown(zip) && !isBlankOrUnknown(city) && !isBlankOrUnknown(state)
}

function syncQuestionOrder(targetQuestionId = currentQuestionId.value) {
  const nextOrder = getQuestionOrder({
    shortForm: isShortForm,
    includeAddress: isShortForm && requiresAddressFallback.value
  })

  questionOrder.value = nextOrder
  localStorage.setItem('questionOrder', JSON.stringify(nextOrder))

  if (!targetQuestionId) return

  const nextIndex = nextOrder.indexOf(targetQuestionId)
  if (nextIndex !== -1) {
    currentProgress.value = nextIndex
  }
}

function isQuestionAnswered(questionId) {
  switch (questionId) {
    case 'amount':
      return questionState.amount.value !== null
    case 'income':
    case 'employed':
      return questionState[questionId].value !== null
    case 'name':
      return !!(questionState.name.first && questionState.name.last)
    case 'email':
      return !!questionState.email.value
    case 'phone':
      return !!(questionState.phone.maskaObject?.completed || questionState.phone.value)
    case 'address':
      return !!(questionState.address.address && questionState.address.zip)
    case 'dob':
      return !!(questionState.dob.day && questionState.dob.month && questionState.dob.year)
    default:
      return false
  }
}

// Compute validated status for current question
function computeValidated(questionId, state) {
  switch (questionId) {
    case 'amount':
      return state.value !== null
    case 'income':
    case 'employed':
      return state.value !== null
    case 'name':
      return !!(state.first && state.last)
    case 'email':
      return !!state.value
    case 'phone':
      return state.overrideValidation ? false : !!(state.maskaObject?.completed)
    case 'address':
      return state.overrideValidation ? false : !!(state.address && state.zip)
    case 'dob':
      return !!(state.day && state.month && state.year)
    default:
      return state.value !== null && state.value !== undefined
  }
}

// Sync config and computed properties into questionState when question changes
// This runs as a side-effect outside of computed to avoid mutation-in-computed issues
watch(
  [currentQuestionId, nameChatMessage, () => sessionStore.empMode],
  ([questionId, chatMsg, empMode]) => {
    if (!questionId) return

    const baseQuestion = getQuestionConfig(questionId, {
      empMode,
      spMode: isSpMode,
      nameChatMessage: chatMsg
    })
    if (!baseQuestion) return

    const state = questionState[questionId]
    if (!state) return

    // Merge config properties into the state object
    Object.assign(state, baseQuestion)
    if (questionId === 'address') {
      state.requireLocationDetails = isShortForm
    }
    state.submit = () => handleQuestionSubmit(questionId)
  },
  { immediate: true }
)

// Get current question - returns the reactive questionState object
// Config is synced via the watch above, computed properties are added here
const currentQuestion = computed(() => {
  const questionId = currentQuestionId.value
  if (!questionId) return null

  const state = questionState[questionId]
  if (!state) return null

  // Add computed properties that need to update reactively
  state.validated = computeValidated(questionId, state)

  if (questionId === 'amount') {
    state.dollarValue = sliderToDollarValue(state.value)
    state.numberValue = sliderToNumberValue(state.value)
    state.backgroundGradientValue = calculateSliderGradient(state.value, state.min, state.max)
  }

  return state
})

const advertiserDetails = computed(() => {
  return advertiserMapping[advertiser.value] || null
})

// Progress percentage for header
const progressPercent = computed(() => {
  return Math.round((currentProgress.value / questionOrder.value.length) * 100)
})

const showHeaderProgress = computed(() => {
  return !displayThankYou.value && !!currentQuestion.value?.showProgress
})

const showHeaderBackButton = computed(() => {
  return !displayThankYou.value && backButtonVisible.value
})

// Show disclaimer for specific affiliates
const showDisclaimer = computed(() => {
  return DISCLAIMER_AIDS.includes(sessionStore.affiliateId)
})

// Check if this affiliate skips credit pull
const skipCreditPull = computed(() => {
  return SKIP_CREDIT_AIDS.includes(sessionStore.affiliateId)
})

// Navigation functions
function proceed() {
  if (isRemovingQuestion.value || showSpinner.value || displayThankYou.value) {
    return
  }

  if (currentProgress.value < questionOrder.value.length - 1) {
    transitionDirection.value = 'forwards'
    isRemovingQuestion.value = true

    // Use requestAnimationFrame to ensure the removal triggers the leave animation
    // Then after a brief delay, show the next question
    requestAnimationFrame(() => {
      setTimeout(() => {
        currentProgress.value++
        isRemovingQuestion.value = false

        // Scroll to top
        document.getElementById('header')?.scrollIntoView()

        // Update URL hash
        history.pushState(
          { currentProgress: currentProgress.value },
          '',
          '#' + questionOrder.value[currentProgress.value]
        )
        localStorage.setItem('questionOrder', JSON.stringify(questionOrder.value))

        // Show back button after first question
        if (currentProgress.value > 0) {
          backButtonVisible.value = true
        }
      }, 50) // Small delay to allow the leave animation to start
    })
  }
}

function goBack() {
  if (currentProgress.value > 0) {
    transitionDirection.value = 'backwards'
    isRemovingQuestion.value = true

    setTimeout(() => {
      currentProgress.value--
      isRemovingQuestion.value = false

      // Scroll to top
      document.getElementById('header')?.scrollIntoView()

      // Update URL hash
      history.pushState(
        { currentProgress: currentProgress.value },
        '',
        '#' + questionOrder.value[currentProgress.value]
      )

      // Hide back button on first question
      if (currentProgress.value === 0) {
        backButtonVisible.value = false
      }
    }, 10)
  }
}

function hideBackButton() {
  backButtonVisible.value = false
}

function showBackButton() {
  backButtonVisible.value = true
}

function showTrackingDisclosure() {
  trackingDisclosureVisible.value = true
}

function hideTrackingDisclosure() {
  trackingDisclosureVisible.value = false
}

// Question submit handler - reads from questionState
async function handleQuestionSubmit(questionId) {
  if (isSubmittingQuestion.value || isRemovingQuestion.value || showSpinner.value || displayThankYou.value) {
    return
  }

  isSubmittingQuestion.value = true
  const state = questionState[questionId]
  console.log('Question submit:', questionId, state)

  try {
    // Update form store based on question type
    switch (questionId) {
      case 'amount':
        formStore.setValue('amount', state.value)
        localStorage.setItem('amount', JSON.stringify(state.value))
        break

      case 'income':
        formStore.setValue('income', state.value)
        localStorage.setItem('income', JSON.stringify(state.value))
        break

      case 'employed':
        formStore.setValue('employed', state.value)
        localStorage.setItem('employed', JSON.stringify(state.value))
        break

      case 'name':
        formStore.setName(state.first, state.last)
        localStorage.setItem('name_first', JSON.stringify(state.first))
        localStorage.setItem('name_last', JSON.stringify(state.last))
        // Fetch location after name
        await fetchLocationData()
        if (isShortForm) {
          requiresAddressFallback.value = !hasLocationData()
          syncQuestionOrder(questionId)
        }
        break

      case 'email':
        formStore.setEmail(state.value, state.sendtoAC)
        localStorage.setItem('email', JSON.stringify(state.value))
        localStorage.setItem('sendtoAC', JSON.stringify(state.sendtoAC))
        // Send to BlueShift
        if (state.sendtoAC) {
          await handleEmailSubmit(state.value)
        }
        break

      case 'address':
        formStore.setAddress(state.address, state.zip, state.city, state.state)
        localStorage.setItem('address', JSON.stringify(state.address))
        localStorage.setItem('zip', JSON.stringify(state.zip))
        localStorage.setItem('city', JSON.stringify(state.city))
        localStorage.setItem('state', JSON.stringify(state.state))
        // Send address update to BlueShift
        if (questionState.email.sendtoAC) {
          sendAddressUpdate({
            email: questionState.email.value,
            state: state.state,
            zip: state.zip
          })
        }
        break

      case 'dob':
        formStore.setDob(state.month, state.day, state.year)
        localStorage.setItem('dob_month', JSON.stringify(state.month))
        localStorage.setItem('dob_day', JSON.stringify(state.day))
        localStorage.setItem('dob_year', JSON.stringify(state.year))
        // Fetch Array data after DOB (non-blocking - don't wait for response)
        if (!isShortForm) {
          fetchArrayDataAfterDob()
        }
        break

      case 'phone':
        const phoneValue = state.maskaObject?.unmasked || state.value?.replace(/\D/g, '') || ''
        formStore.setPhone(phoneValue, state.tcpaLanguage, state.validationStatus)
        localStorage.setItem('phone', JSON.stringify(phoneValue))
        // Handle phone submission and lead posting
        await handlePhoneSubmit({
          phone: phoneValue,
          tcpaLanguage: state.tcpaLanguage,
          validationStatus: state.validationStatus
        })
        return // Don't proceed here, handlePhoneSubmit handles navigation

      default:
        if (state && state.value !== undefined) {
          formStore.setValue(questionId, state.value)
          localStorage.setItem(questionId, JSON.stringify(state.value))
        }
    }

    proceed()
  } finally {
    isSubmittingQuestion.value = false
  }
}

// Fetch location data
async function fetchLocationData() {
  try {
    const location = await fetchLocation()
    if (location) {
      sessionStore.setIpAddress(location.ip)
      sessionStore.setLocationBackup({ city: location.city, state: location.state, zip: location.zip })

      if (isShortForm) {
        const nextZip = isBlankOrUnknown(questionState.address.zip) ? location.zip : questionState.address.zip
        const nextCity = isBlankOrUnknown(questionState.address.city) ? location.city : questionState.address.city
        const nextState = isBlankOrUnknown(questionState.address.state) ? location.state : questionState.address.state

        if (!isBlankOrUnknown(nextZip) || !isBlankOrUnknown(nextCity) || !isBlankOrUnknown(nextState)) {
          questionState.address.zip = isBlankOrUnknown(nextZip) ? '' : nextZip
          questionState.address.city = isBlankOrUnknown(nextCity) ? '' : nextCity
          questionState.address.state = isBlankOrUnknown(nextState) ? '' : nextState
          formStore.setAddress(undefined, questionState.address.zip, questionState.address.city, questionState.address.state)
        }
      }
    }
    return location
  } catch (error) {
    console.error('Error fetching location:', error)
    return null
  }
}

// Fetch Array data after DOB
async function fetchArrayDataAfterDob() {
  if (isShortForm) {
    return
  }

  // Wait for IP if not available yet
  if (!sessionStore.ipAddress) {
    await fetchLocationData()
  }

  try {
    const campaignKeys = getCampaignKeys()
    const configuredCreditPull = getCreditPullSettingsForCampaign(campaignKeys)

    // Check for ?crs=1 URL param to force CRS API call
    const forceCRS = params.get('crs') === '1'
    leadStore.setForceCRS(forceCRS)

    const creditPullSettings = forceCRS
      ? { primary: 'crs', fallback: 'off' }
      : configuredCreditPull

    // Skip Array/CRS fetch if sa=1 param, affiliate is in skip list, or campaign is configured off
    const skipArray = params.get('sa') === '1' || skipCreditPull.value || creditPullSettings.primary === 'off'
    if (skipArray) {
      console.log(
        'Skipping Array/CRS fetch',
        skipCreditPull.value
          ? '(skip-credit affiliate)'
          : creditPullSettings.primary === 'off'
            ? '(campaign creditPull=off)'
            : '(sa=1)'
      )

      // For configured skip-credit campaigns, set verified debt from user-selected amount
      if (skipCreditPull.value || creditPullSettings.primary === 'off') {
        const userDebt = sliderToNumberValue(questionState.amount.value)
        leadStore.setVerifiedDebt(userDebt)
        console.log('Set verified_debt from user input:', userDebt)
      }
      return
    }

    const dobIso = toIsoDob(questionState.dob.month, questionState.dob.day, questionState.dob.year)
    const response = await fetchArrayData({
      firstName: questionState.name.first,
      lastName: questionState.name.last,
      address: questionState.address.address,
      city: questionState.address.city || sessionStore.backupCity,
      state: questionState.address.state || sessionStore.backupState,
      zip: questionState.address.zip,
      dob: dobIso,
      ipAddress: sessionStore.ipAddress,
      forceCRS,
      creditPullPrimary: creditPullSettings.primary,
      creditPullFallback: creditPullSettings.fallback
    })

    if (response) {
      // Parse the array data from Lambda response
      const parsedArray = parseArrayResponse(response.array)

      if (parsedArray) {
        arrayData.value = {
          collections: parsedArray.collections || null,
          credit_score: parsedArray.credit_score || null,
          delinquencies: parsedArray.delinquencies || null,
          total_unsecured_debt: parsedArray.total_unsecured_debt || null,
          tax_liens: parsedArray.tax_liens || null,
          open_student_loan_bal: parsedArray.open_student_loan_bal || null
        }

        // Send Array data to BlueShift
        if (questionState.email.sendtoAC) {
          sendArrayData({
            email: questionState.email.value,
            arrayDebt: parsedArray.total_unsecured_debt,
            creditScore: parsedArray.credit_score,
            collections: parsedArray.collections,
            delinquencies: parsedArray.delinquencies,
            taxLiens: parsedArray.tax_liens,
            studentLoans: parsedArray.open_student_loan_bal,
            birthYear: questionState.dob.year,
            birthMonth: questionState.dob.month,
            birthDay: questionState.dob.day
          })
        }
      }

      // Store CRS data in leadStore
      if (response.crs !== undefined) {
        leadStore.setCrsData(response.crs)
      }
      if (response.verified_debt !== undefined) {
        leadStore.setVerifiedDebt(response.verified_debt)
      }
      if (response.crsError !== undefined) {
        leadStore.setCrsError(response.crsError)
      }
      if (response.crsFallback) {
        leadStore.setCrsFallback(response.crsFallback)
      }
      if (response.strategy !== undefined) {
        leadStore.setCreditPullStrategy({
          ...response.strategy,
          mode: response.experiment?.mode || response.strategy?.mode
        })
      }
    }
  } catch (error) {
    console.error('Error fetching Array data:', error)
  }
}

// Handle email submit
async function handleEmailSubmit(email) {
  try {
    // Send to BlueShift
    await sendEmailSignup({
      email,
      ip: sessionStore.ipAddress,
      firstname: questionState.name.first,
      lastname: questionState.name.last,
      debt: sliderToNumberValue(questionState.amount.value),
      income: questionState.income.value
    })

    // Trigger BlueShift event
    await triggerFafSignup(email)
  } catch (error) {
    console.error('Error handling email submit:', error)
  }
}

async function waitForEverflowTransaction(campaignKeys) {
  if (!campaignKeys.everflow_aff_id || sessionStore.formData.lp_s5) return

  if (everflowInitPromise.value) {
    await Promise.race([
      everflowInitPromise.value.catch(() => ''),
      new Promise((resolve) => setTimeout(resolve, 1500))
    ])
  }

  if (sessionStore.formData.lp_s5) return

  const recoveredTransactionId = recoverEverflowTransactionId({
    affId: campaignKeys.everflow_aff_id,
    offerId: campaignKeys.everflow_offer_id || 320
  })

  if (recoveredTransactionId) {
    sessionStore.setLpS5(recoveredTransactionId)
    return
  }

  appendEverflowDebugLog({
    affId: campaignKeys.everflow_aff_id,
    offerId: campaignKeys.everflow_offer_id || 320,
    entry: 'si'
  })

  const transactionId = await initEverflow({
    affId: campaignKeys.everflow_aff_id,
    offerId: campaignKeys.everflow_offer_id || 320,
    params,
    attemptLabelPrefix: 's',
    onTransactionId: (value) => {
      sessionStore.setLpS5(value)
    }
  })

  if (transactionId) {
    sessionStore.setLpS5(transactionId)
  }
}

// Handle phone submit and lead posting
async function handlePhoneSubmit(phoneData) {
  if (isShortForm && !hasLocationData()) {
    requiresAddressFallback.value = true
    syncQuestionOrder('address')
    return
  }

  // Capture compliance tokens BEFORE showing spinner (which removes the form from DOM)
  const jornayaToken = document.getElementById('leadid_token')?.value || ''
  const trustedFormToken = document.getElementsByName('xxTrustedFormToken')[0]?.value || ''

  console.log('Compliance tokens captured:', { jornayaToken, trustedFormToken })

  showSpinner.value = true

  try {
    // Send phone update to BlueShift
    if (questionState.email.sendtoAC) {
      await sendPhoneUpdate({
        email: questionState.email.value,
        phone: phoneData.phone.replace(/\D/g, ''),
        firstname: questionState.name.first,
        lastname: questionState.name.last,
        ip: sessionStore.ipAddress
      })

      // Trigger BlueShift fullsubmit event
      await triggerFullSubmit(questionState.email.value)
    }

    // Build lead data using centralized function
    const campaignKeys = getCampaignKeys()
    await waitForEverflowTransaction(campaignKeys)

    // Prepare questionState with computed debt amount
    const questionStateWithDebt = {
      ...questionState,
      amountNumberValue: sliderToNumberValue(questionState.amount.value)
    }

    const leadData = buildLeadData({
      questionState: questionStateWithDebt,
      sessionStore,
      campaignKeys,
      arrayData: arrayData.value,
      phoneData,
      jornayaToken,
      trustedFormToken,
      showArrayDisclaimer: showArrayDisclaimer.value,
      shortForm: isShortForm,
      crsData: leadStore.crsData,
      serverVerifiedDebt: leadStore.verifiedDebt,
      forceCRS: leadStore.forceCRS,
      crsFallback: leadStore.crsFallback,
      creditPullStrategy: leadStore.creditPullStrategy
    })

    // Post lead
    const { response, duplicate } = await processLead({
      dataObj: leadData,
      attemptNumber: postAttemptNumber.value,
      formStore,
      onAttemptIncrement: () => ++postAttemptNumber.value
    })

    // Handle response
    const result = response?.data?.result
    const revenue = Number(response?.data?.price ?? 0)
    const clients = response?.data?.clients
    const redirectUrl = response?.data?.redirect_url

    // Get advertiser info
    let advertiserId = null
    if (clients) {
      advertiserId = getAdvertiserFromClients(clients)
      if (advertiserId && advertiserMapping[advertiserId]) {
        advertiser.value = advertiserId
      }
    }

    // Send to call center if needed
    if (shouldSendToCallCenter(revenue, result)) {
      sendToCallCenter({
        firstname: questionState.name.first,
        lastname: questionState.name.last,
        email: questionState.email.value,
        phone: phoneData.phone.replace(/\D/g, ''),
        city: questionState.address.city || sessionStore.backupCity,
        state: questionState.address.state || sessionStore.backupState,
        zip: questionState.address.zip || sessionStore.backupZip
      })
    }

    // Fire LP pixel for qualifying leads with a request ID
    if (!isShortForm && revenue >= 45 && lpRequestId.value) {
      fireLpPixel(lpRequestId.value)
    }

    // Send buyer info to BlueShift
    if (advertiserDetails.value && questionState.email.sendtoAC) {
      sendBuyerInfo({
        email: questionState.email.value,
        buyer: advertiserDetails.value.buyer,
        buyerPhone: advertiserDetails.value.did
      })

      // Trigger buyer sequence
      await triggerBuyerSequence(questionState.email.value)
    }

    // Store lead info
    leadStore.setSubmitted(true)
    leadStore.setLeadId(response?.data?.lead_id)
    leadStore.setRevenue(revenue)
    leadStore.setAdvertiser(advertiser.value)

    // Clear stored data
    clearStoredData()

    // Handle redirects
    handleRedirects({
      redirectUrl,
      advertiserId,
      revenue,
      leadData
    })

  } catch (error) {
    console.error('Error posting lead:', error)
    showSpinner.value = false
    displayThankYou.value = true
  }
}

// Handle post-submission redirects
function handleRedirects({ redirectUrl, advertiserId, revenue, leadData }) {
  // 1. If Leadspedia returns a redirect URL, always use it
  if (redirectUrl && redirectUrl.startsWith('https://')) {
    performRedirect(redirectUrl.replace(/\\u0026/g, '&'))
    return
  }

  if (isShortForm) {
    if (advertiserId === '2' || advertiserId === '24' || advertiserId === '30' || revenue > 1) {
      showSpinner.value = false
      displayThankYou.value = true
      return
    }

    performRedirect(buildTurboRedirectUrl(leadData))
    return
  }

  // 2. Show thank you page for specific advertisers or if there's revenue
  const showThankYou =
    advertiserId === '2' ||
    (advertiserId === '24' && leadData.verified_debt > 1) ||
    advertiserId === '30' ||
    (advertiserMapping[advertiserId] && revenue > 1)

  if (showThankYou) {
    showSpinner.value = false
    displayThankYou.value = true
    return
  }

  // 3. Otherwise, trigger fallback redirect based on debt/revenue
  const verifiedDebt = leadData.verified_debt
  let redirectUrlCustom = `https://firstadvantage.io/programs/save.html?vd=${verifiedDebt}`

  // Redirect to turbo link for specific conditions
  if (verifiedDebt === 9949 || (verifiedDebt === 0 && revenue < 1) || (verifiedDebt > 10000 && revenue < 1)) {
    redirectUrlCustom = buildTurboRedirectUrl(leadData)
  }

  performRedirect(redirectUrlCustom)
}

function buildTurboRedirectUrl(leadData) {
  const turboLink = 'https://www.ab92trk.com/2X2ZMNN/45TJ3T/?sub1=fa1&sub2='
  const phone = questionState.phone.maskaObject?.unmasked || leadData.phone_home || ''

  return `${turboLink}${leadData.aff_id || ''}&first=${leadData.first_name || ''}&last=${leadData.last_name || ''}&phone=${phone}&email=${leadData.email_address || ''}&state=${leadData.state || ''}`
}

// Perform redirect with small delay to allow tracking pixels to fire
function performRedirect(url) {
  setTimeout(() => {
    window.location.href = url
  }, 500)
}

// Get campaign keys based on affiliate and EMP mode
function getCampaignKeys() {
  const affId = sessionStore.affiliateId

  // Check for EMP split keys
  if (sessionStore.empSplitKeys) {
    return sessionStore.empSplitKeys
  }

  return getCampaignKeysForAff(affId)
}

// Clear stored form data
function clearStoredData() {
  const keysToRemove = [
    'amount', 'income', 'employed',
    'name_first', 'name_last', 'email', 'sendtoAC',
    'address', 'zip', 'city', 'state',
    'dob_month', 'dob_day', 'dob_year',
    'phone', 'questionOrder'
  ]
  keysToRemove.forEach(key => localStorage.removeItem(key))

  const campaignKeys = getCampaignKeys()
  if (campaignKeys.everflow_aff_id) {
    clearStoredEverflowTransactionId({
      affId: campaignKeys.everflow_aff_id,
      offerId: campaignKeys.everflow_offer_id || 320
    })
    clearEverflowDebugLog({
      affId: campaignKeys.everflow_aff_id,
      offerId: campaignKeys.everflow_offer_id || 320
    })
    clearEverflowSdkError({
      affId: campaignKeys.everflow_aff_id,
      offerId: campaignKeys.everflow_offer_id || 320
    })
    clearEverflowClickError({
      affId: campaignKeys.everflow_aff_id,
      offerId: campaignKeys.everflow_offer_id || 320
    })
    sessionStore.setLpS5('')
  }
}

// Handle browser back button
window.addEventListener('popstate', (event) => {
  if (event.state && typeof event.state.currentProgress === 'number') {
    transitionDirection.value = event.state.currentProgress < currentProgress.value ? 'backwards' : 'forwards'
    isRemovingQuestion.value = true

    setTimeout(() => {
      currentProgress.value = event.state.currentProgress
      isRemovingQuestion.value = false

      // Update back button visibility
      backButtonVisible.value = currentProgress.value > 0

      document.getElementById('header')?.scrollIntoView()
    }, 10)
  }
})

// Safe JSON parse with fallback (backwards compatibility)
function safeJsonParse(value, fallback = '') {
  if (!value) return fallback
  try {
    return JSON.parse(value)
  } catch {
    return value // Return raw value if not valid JSON
  }
}

// Load stored form data on mount
function loadStoredFormData() {
  if (isShortForm) {
    setShortFormIncome()
  }

  // Load simple values
  const storedAmount = localStorage.getItem('amount')
  if (amountFromDebtParam !== null) {
    questionState.amount.value = amountFromDebtParam
    formStore.values.amount = amountFromDebtParam
  } else if (storedAmount) {
    const val = safeJsonParse(storedAmount)
    questionState.amount.value = val
    formStore.setValue('amount', val)
  }

  const storedIncome = localStorage.getItem('income')
  if (!isShortForm && storedIncome) {
    const val = safeJsonParse(storedIncome)
    questionState.income.value = val
    formStore.setValue('income', val)
  }

  const storedEmployed = localStorage.getItem('employed')
  if (storedEmployed) {
    const val = safeJsonParse(storedEmployed)
    questionState.employed.value = val
    formStore.setValue('employed', val)
  }

  // Load name
  const storedFirst = localStorage.getItem('name_first')
  const storedLast = localStorage.getItem('name_last')
  if (storedFirst || storedLast) {
    const first = safeJsonParse(storedFirst, '')
    const last = safeJsonParse(storedLast, '')
    questionState.name.first = first
    questionState.name.last = last
    formStore.setName(first, last)
  }

  // Load email
  const storedEmail = localStorage.getItem('email')
  const storedSendtoAC = localStorage.getItem('sendtoAC')
  if (storedEmail) {
    const email = safeJsonParse(storedEmail, '')
    const sendtoAC = safeJsonParse(storedSendtoAC, true)
    questionState.email.value = email
    questionState.email.sendtoAC = sendtoAC
    formStore.setEmail(email, sendtoAC)
  }

  // Load address
  const storedAddress = localStorage.getItem('address')
  const storedZip = localStorage.getItem('zip')
  const storedCity = localStorage.getItem('city')
  const storedState = localStorage.getItem('state')
  if (storedAddress || storedZip) {
    const address = safeJsonParse(storedAddress, '')
    const zip = safeJsonParse(storedZip, '')
    const city = safeJsonParse(storedCity, '')
    const state = safeJsonParse(storedState, '')
    questionState.address.address = address
    questionState.address.zip = zip
    questionState.address.city = city
    questionState.address.state = state
    formStore.setAddress(address, zip, city, state)
  }

  // Load DOB
  const storedMonth = localStorage.getItem('dob_month')
  const storedDay = localStorage.getItem('dob_day')
  const storedYear = localStorage.getItem('dob_year')
  if (storedMonth || storedDay || storedYear) {
    const month = safeJsonParse(storedMonth, '')
    const day = safeJsonParse(storedDay, '')
    const year = safeJsonParse(storedYear, '')
    questionState.dob.month = month
    questionState.dob.day = day
    questionState.dob.year = year
    formStore.setDob(month, day, year)
  }

  // Load phone
  const storedPhone = localStorage.getItem('phone')
  if (storedPhone) {
    const phone = safeJsonParse(storedPhone, '')
    questionState.phone.value = phone
    questionState.phone.maskaObject.unmasked = phone ? phone.replace(/\D/g, '') : ''
    questionState.phone.maskaObject.completed = questionState.phone.maskaObject.unmasked.length === 10
    formStore.setPhone(phone)
  }
}

// Restore progress from URL hash
function restoreProgressFromHash() {
  if (!window.location.hash) return

  const storedQuestionOrder = localStorage.getItem('questionOrder')
  if (!isShortForm && storedQuestionOrder) {
    questionOrder.value = safeJsonParse(storedQuestionOrder, questionOrder.value)
  }

  const hashQuestion = window.location.hash.replace('#', '')
  let targetProgress = questionOrder.value.indexOf(hashQuestion)

  if (targetProgress === -1) return

  // Verify all previous questions are answered
  for (let i = 0; i < targetProgress; i++) {
    const qId = questionOrder.value[i]

    if (qId === 'name') {
      if (!formStore.name.first || !formStore.name.last) {
        targetProgress = i
        break
      }
    } else if (!isQuestionAnswered(qId)) {
      targetProgress = i
      break
    }

    history.pushState({ currentProgress: i }, '', '#' + qId)
  }

  currentProgress.value = targetProgress

  // Fetch data if we have enough info
  if (formStore.name.first) {
    fetchLocationData()
  }

  if (!isShortForm && formStore.dob.day) {
    fetchArrayDataAfterDob()
  }
}

// Initialize on mount
onMounted(async () => {
  if (isThankYouPreview) {
    advertiser.value = thankYouPreviewAdvertiserId
    displayThankYou.value = true
    backButtonVisible.value = false
    return
  }

  // Check for redirect affiliates
  if (checkDirectLinkRedirect()) return

  // Load stored form data
  loadStoredFormData()

  if (isShortForm) {
    syncQuestionOrder()
  }

  // Initialize pixels
  initTrustedForm()

  // Initialize Everflow if applicable
  const campaignKeys = getCampaignKeysForAff(sessionStore.affiliateId)
  if (campaignKeys.everflow_aff_id) {
    everflowInitPromise.value = initEverflowWithClickTracking({
      offerId: campaignKeys.everflow_offer_id || 320,
      affId: campaignKeys.everflow_aff_id,
      params,
      clickCampaignId: campaignKeys.click_campaign_id,
      onTransactionId: (transactionId) => {
        sessionStore.setLpS5(transactionId)
      },
      onRequestId: (requestId) => {
        lpRequestId.value = requestId
      }
    })
  } else if (campaignKeys.click_campaign_id) {
    everflowInitPromise.value = null
    loadLpClickScript({
      clickCampaignId: campaignKeys.click_campaign_id,
      lpS5: sessionStore.formData.lp_s5,
      lpS2: sessionStore.formData.lp_s2,
      onRequestId: (requestId) => {
        lpRequestId.value = requestId
      }
    })
  } else {
    everflowInitPromise.value = null
  }

  // Initialize RedTrack for kfb1/gs affiliates when cmpid exists and s2 is empty
  const cmpid = params.get('cmpid')
  const s2 = params.get('s2')
  if ((sessionStore.affiliateId === 'kfb1' || sessionStore.affiliateId === 'gs') && cmpid && !s2) {
    initRedTrackPixel(cmpid).then((clickId) => {
      if (clickId) {
        sessionStore.setLpS2(clickId)
        console.log('RedTrack: lp_s2 set to', clickId)
      }
    })
  }

  if (isShortForm && formStore.name.first) {
    await fetchLocationData()
    requiresAddressFallback.value = !hasLocationData()
    syncQuestionOrder()
  }

  // Restore progress from URL hash
  restoreProgressFromHash()

  // Set initial history state
  history.pushState(
    { currentProgress: currentProgress.value },
    '',
    '#' + questionOrder.value[currentProgress.value]
  )
})
</script>

<template>
  <Header
    :progressPercent="progressPercent"
    :showProgress="showHeaderProgress"
    :backButtonVisible="showHeaderBackButton"
    :advertiser="advertiser"
    :showDisclaimer="showDisclaimer"
    @showTrackingDisclosure="showTrackingDisclosure"
    @goBack="goBack" />
  <Main
    v-if="!showSpinner && !displayThankYou"
    :question="currentQuestion"
    :removeQuestion="isRemovingQuestion"
    :direction="transitionDirection"
    :backButtonVisible="backButtonVisible"
    :firstName="questionState.name.first"
    :mainHeadline="mainHeadline"
    :mainSubHeadline="mainSubHeadline"
    :showArrayDisclaimer="showArrayDisclaimer"
    :trackingDisclosureVisible="trackingDisclosureVisible"
    @showTrackingDisclosure="showTrackingDisclosure"
    @hideTrackingDisclosure="hideTrackingDisclosure"
    @proceed="currentQuestion?.submit"
    @goBack="goBack"
    @hideBackButton="hideBackButton"
    @showBackButton="showBackButton" />
  <section v-else-if="showSpinner && !displayThankYou" class="spinnerContainer">
    <Spinner :messages="['Verifying your information...','Checking if you qualify...','Calculating your savings...','Validating results...','Looking for more savings...','Almost there!']" />
  </section>
  <Thankyou
    v-else-if="displayThankYou"
    :advertiser="advertiser"
  />
  <Footer />
</template>

<style scoped>
  .spinnerContainer {
    min-height: 550px;
    min-height: 70vh;
    position: relative;
    padding-top: 50px;
  }
</style>
